import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { catchError, firstValueFrom, map, of, tap } from 'rxjs';

import { ValidationService } from '../../../utils/valibot/validation.service';
import { VehicleSearchQuery } from '../../model/customer-search-query.model';
import { RetailCrmAccountQueryOptions, RetailCrmNewAccountInput } from './model/requests/accounts.model';
import { RetailCrmAccountUpdateInput } from './model/requests/update-account.model';
import { UpdateC2vRequest } from './model/requests/update-c2v.model';
import {
  RetailCrmAccountDetailsResponse,
  RetailCrmAccountDetailsResponseSchema,
} from './model/responses/account-details.model';
import {
  RetailCrmCreateAccountResponse,
  RetailCrmCreateAccountResponseSchema,
} from './model/responses/create-account.model';
import { RetailCrmAccountsResponse, RetailCrmAccountsResponseSchema } from './model/responses/get-accounts.model';
import {
  RetailCrmRelatedDealersResponse,
  RetailCrmRelatedDealersResponseSchema,
} from './model/responses/legal-entities.model';
import { RetailCrmSyncToGcdmResponse, RetailCrmSyncToGcdmResponseSchema } from './model/responses/sync-to-gcdm.model';
import {
  RetailCrmUpdateAccountResponse,
  RetailCrmUpdateAccountResponseSchema,
} from './model/responses/update-account.model';
import {
  RetailCrmVehicle,
  RetailCrmVehicleSchema,
  RetailCrmVehiclesResponse,
  RetailCrmVehiclesResponseSchema,
} from './model/responses/vehicles.model';

/**
 * For external APIs like this we decided to only soft-validate responses using Valibot schemas.
 */
@Injectable({
  providedIn: 'root',
})
export class RetailCrmClient {
  private static readonly urlPrefix = `retail/crm`;

  private readonly http = inject(HttpClient);
  private readonly logger = inject(NGXLogger);
  private readonly validationService = inject(ValidationService);

  searchAccounts(options: RetailCrmAccountQueryOptions): Promise<RetailCrmAccountsResponse> {
    let queryParams = new HttpParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value != undefined) {
        queryParams = queryParams.set(key, value as string);
      }
    });

    const url = `${RetailCrmClient.urlPrefix}/accounts`;
    this.logger.info(`#CDFTF0007 Searching for accounts ${url}`);
    return firstValueFrom(
      this.http
        .get(url, {
          params: queryParams,
        })
        .pipe(
          this.validationService.pipeResponseSoftFail(RetailCrmAccountsResponseSchema),
          catchError((error: HttpErrorResponse) => {
            if (error.status === 404) {
              return of([]);
            } else {
              this.logger.info('#CDFTF0008 Unable to search for accounts', error);
              throw error;
            }
          }),
        ),
    );
  }

  getAccountByAccountNumber(accountNumber: string): Promise<RetailCrmAccountDetailsResponse> {
    const url = `${RetailCrmClient.urlPrefix}/accounts/${accountNumber}`;
    this.logger.info(`#CDFTF0095 Getting account details by ID ${url}`);
    return firstValueFrom(
      this.http.get(url).pipe(
        this.validationService.pipeResponseSoftFail(RetailCrmAccountDetailsResponseSchema),
        tap({
          error: (error) => this.logger.error('#CDFTF0096 Unable to get account details', error),
        }),
      ),
    );
  }

  createAccount(newAccount: RetailCrmNewAccountInput): Promise<RetailCrmCreateAccountResponse> {
    const url = `${RetailCrmClient.urlPrefix}/accounts`;
    this.logger.info(`#CDFTF0020 Creating new account ${url}`);
    return firstValueFrom(
      this.http.post(url, newAccount).pipe(
        this.validationService.pipeResponseSoftFail(RetailCrmCreateAccountResponseSchema),
        tap((response) => {
          if (!response.success) {
            throw new Error(`#CDFTF0042 Failed to create account ${JSON.stringify(response)}`);
          }
        }),
        tap({
          error: (error) => this.logger.error('#CDFTF0021 Unable to create account', error),
        }),
      ),
    );
  }

  searchVehicles(query: VehicleSearchQuery): Promise<RetailCrmVehiclesResponse> {
    let queryParams = new HttpParams();
    if (query.vin) {
      queryParams = queryParams.set('vin', query.vin);
    }
    if (query.licenceNumber) {
      queryParams = queryParams.set('registrationNumber', query.licenceNumber);
    }

    const url = `${RetailCrmClient.urlPrefix}/vehicles`;
    this.logger.info(`#CDFTF0046 Searching for vehicles ${url}`);
    return firstValueFrom(
      this.http
        .get(url, {
          params: queryParams,
        })
        .pipe(
          this.validationService.pipeResponseSoftFail(RetailCrmVehiclesResponseSchema),
          catchError((error: HttpErrorResponse) => {
            if (error.status === 404) {
              return of([]);
            } else {
              this.logger.error('#CDFTF0047 Could not search for vehicles', error);
              throw error;
            }
          }),
        ),
    );
  }

  getVehicle(vin: string): Promise<RetailCrmVehicle | null> {
    const url = `${RetailCrmClient.urlPrefix}/vehicles/${vin}`;
    this.logger.info(`#CDFTF0250 Getting vehicle ${url}`);
    return firstValueFrom(
      this.http.get(url).pipe(
        this.validationService.pipeResponseSoftFail(RetailCrmVehicleSchema),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            this.logger.warn(`#CDFFF0252 Vehicle with VIN ${vin} not found`);
            return of(null);
          }
          this.logger.error('#CDFTF0251 Failed to get vehicle info', error);
          throw error;
        }),
      ),
    );
  }

  updateVehicleC2v(request: UpdateC2vRequest): Promise<void> {
    const { vin, ...body } = request;
    const url = `${RetailCrmClient.urlPrefix}/vehicles/${vin}`;
    this.logger.info(`#CDFTF0227 Updating C2V ${url}`);
    return firstValueFrom(
      this.http.put<void>(url, body).pipe(
        catchError((error: HttpErrorResponse) => {
          this.logger.error('#CDFTF0228 Failed to update C2V relation', error);
          throw error;
        }),
      ),
    );
  }

  importFromGcdm(gcid: string): Promise<string | undefined> {
    const url = `${RetailCrmClient.urlPrefix}/import`;
    this.logger.info(`#CDFTF0048 Importing from NSC Database ${url}`);
    return firstValueFrom(
      this.http.post(url, { gcid }).pipe(
        this.validationService.pipeResponseSoftFail(RetailCrmCreateAccountResponseSchema),
        tap((response) => {
          if (!response.success) {
            throw new Error(`#CDFTF0049 Failed to import from NSC Database ${JSON.stringify(response)}`);
          }
        }),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('#CDFTF0050 Failed to import from NSC Database', error);
          throw error;
        }),
        map((response) => response.accountNumber),
      ),
    );
  }

  updateAccount(
    accountNumber: string,
    accountUpdateInput: RetailCrmAccountUpdateInput,
  ): Promise<RetailCrmUpdateAccountResponse> {
    const url = `${RetailCrmClient.urlPrefix}/update/${accountNumber}`;
    this.logger.info(`#CDFTF0077 Updating account ${url}`);
    return firstValueFrom(
      this.http.put(url, accountUpdateInput).pipe(
        this.validationService.pipeResponseSoftFail(RetailCrmUpdateAccountResponseSchema),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('#CDFTF0078 Unable to update account', error);
          throw error;
        }),
        tap((response) => {
          if (!response.success) {
            throw new Error(`#CDFTF0079 Failed to update account from ESO ${JSON.stringify(response)}`);
          }
        }),
      ),
    );
  }

  /**
   * Links a Retail CRM account to a business partner in the NSC Database (GCDM).
   */
  syncWithGcdm(accountNumber: string, gcid: string): Promise<RetailCrmSyncToGcdmResponse> {
    const url = `${RetailCrmClient.urlPrefix}/gcdm-sync/${accountNumber}/${gcid}`;
    this.logger.info(`#CDFTF0267 Linking Retail CRM account to the NSC Database ${url}`);
    return firstValueFrom(
      this.http.put(url, null).pipe(
        this.validationService.pipeResponseSoftFail(RetailCrmSyncToGcdmResponseSchema),
        tap((response) => {
          if (!response.success) {
            throw new Error(
              `#CDFTF0278 Failed to link Retail CRM account to the NSC Database ${JSON.stringify(response)}`,
            );
          }
        }),
        catchError((error: unknown) => {
          this.logger.error('#CDFTF0268 Could not link Retail CRM account to the NSC Database', error);
          throw error;
        }),
      ),
    );
  }

  getRelatedDealers(): Promise<RetailCrmRelatedDealersResponse> {
    const url = `${RetailCrmClient.urlPrefix}/related-dealers`;
    this.logger.info(`#CDFTF0179 Fetching legal entities in same group: ${url}`);
    return firstValueFrom(
      this.http.get(url).pipe(
        this.validationService.pipeResponseSoftFail(RetailCrmRelatedDealersResponseSchema),
        tap({
          error: (error) => this.logger.error(`#CDFTF0180 Unable to fetch legal entities in same group`, error),
        }),
      ),
    );
  }
}
